import React, { useEffect, useState } from "react";
import { personnelApi } from "../../api/personnelApi";
import ExpiringContractsWidget from "./widgets/ExpiringContractsWidget";

const GENDER_LABELS = { MALE: "Maschio", FEMALE: "Femmina", OTHER: "Altro" };
const CONTRACT_LABELS = {
  FIXED_TERM: "A tempo determinato",
  PERMANENT: "A tempo indeterminato",
  SEASONAL: "Stagionale",
  APPRENTICESHIP: "Apprendistato",
  OTHER: "Altro",
};

export default function EmployeeDetails({ employee }) {
  const [contracts, setContracts] = useState([]);
  const [leave, setLeave] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!employee?.id) return;
    setLoading(true);
    setError("");
    Promise.all([
      personnelApi.listContracts(employee.id).catch(() => []),
      personnelApi.getLeave(employee.id).catch(() => null),
    ])
      .then(([c, l]) => {
        setContracts(Array.isArray(c) ? c : []);
        setLeave(l);
      })
      .catch((e) => {
        setError(e?.message || "Errore caricamento dettagli dipendente");
      })
      .finally(() => setLoading(false));
  }, [employee?.id]);

  const residualLeave =
    leave != null
      ? Math.max(0, Number(leave.accruedDays ?? 0) - Number(leave.usedDays ?? 0))
      : null;

  return (
    <div>
      <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-2">
        <div>
          <h5 className="mb-0">
            {employee.lastName} {employee.firstName}
          </h5>
          <div className="text-muted small">
            {employee.email || "—"} {employee.phone ? ` • ${employee.phone}` : ""}
          </div>
          {employee.matricola && (
            <div className="text-muted small">
              Matricola: <code>{employee.matricola}</code>
            </div>
          )}
        </div>

        <ExpiringContractsWidget />
      </div>

      {/* Anagrafica completa */}
      <div className="card mb-3">
        <div className="card-header fw-semibold">Anagrafica</div>
        <div className="card-body small">
          <div className="row g-2">
            <div className="col-12 col-md-6">
              <strong>Indirizzo completo:</strong> {employee.address || "—"}
            </div>
            <div className="col-12 col-md-6">
              <strong>Comune di nascita:</strong> {employee.birthPlace || "—"}
            </div>
            <div className="col-12 col-md-6">
              <strong>Data di nascita:</strong>{" "}
              {employee.birthDate ? new Date(employee.birthDate).toLocaleDateString("it-IT") : "—"}
            </div>
            <div className="col-12 col-md-6">
              <strong>Genere:</strong> {employee.gender ? GENDER_LABELS[employee.gender] || employee.gender : "—"}
            </div>
            <div className="col-12 col-md-6">
              <strong>Codice fiscale:</strong> {employee.fiscalCode || "—"}
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-muted small mb-3">
          <span className="spinner-border spinner-border-sm me-1" />
          Caricamento contratti e ferie…
        </div>
      )}
      {error && <div className="alert alert-danger py-2 small mb-3">{error}</div>}

      <div className="row g-3">
        <div className="col-12 col-md-6">
          <div className="card h-100">
            <div className="card-header fw-semibold">Contratti</div>
            <div className="card-body">
              {!contracts.length ? (
                <div className="text-muted small">Nessun contratto presente.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Tipo</th>
                        <th>Inizio</th>
                        <th>Fine</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contracts.map((c) => (
                        <tr key={c.id}>
                          <td>{CONTRACT_LABELS[c.type] || c.type}</td>
                          <td>{c.startDate ? new Date(c.startDate).toLocaleDateString("it-IT") : "—"}</td>
                          <td>{c.endDate ? new Date(c.endDate).toLocaleDateString("it-IT") : "—"}</td>
                          <td className="small text-muted">{c.notes || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6">
          <div className="card h-100">
            <div className="card-header fw-semibold">Ferie e permessi</div>
            <div className="card-body">
              {!leave ? (
                <div className="text-muted small">Nessun dato ferie registrato.</div>
              ) : (
                <ul className="list-unstyled small mb-0">
                  <li>
                    <strong>Giorni maturati ferie:</strong> {Number(leave.accruedDays ?? 0)} gg
                  </li>
                  <li>
                    <strong>Ferie usate:</strong> {Number(leave.usedDays ?? 0)} gg
                  </li>
                  <li>
                    <strong>Ferie residue:</strong>{" "}
                    {residualLeave != null ? `${residualLeave} gg` : "—"}
                  </li>
                  <li>
                    <strong>Permessi usati:</strong>{" "}
                    {leave.permessiDays != null ? `${Number(leave.permessiDays)} gg` : "—"}
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="text-muted small mt-3">
        Per gestire paghe, extra e cedolini usa le sezioni dedicate nell&apos;area personale.
      </div>
    </div>
  );
}
